import { BIOMES, biomeForLevel, type BiomeId } from '../content/biomes';
import { BOOSTS } from '../content/boosts';
import { MOWER_DEFS, MOWER_IDS, type MowerId } from '../content/mowers';
import { TOOLS, type ToolId } from '../content/tools';
import type { BoostId } from '../sim/boosts';
import type { CritterKind } from '../sim/critters';
import {
  campaignProgress,
  totalStars,
  UPGRADE_LEVEL_OPENS,
  upgradeLevelOpensAfter,
  UPGRADES,
  type UpgradeKind,
  type ZoneReward,
} from './economy';
import type { Save } from './save/schema';
import { BUILDER_SHOP } from './customLawns';
import { mowerNeedsFullGame } from './unlock';

/**
 * The moments after a lawn worth a screen of their own (owner, 2026-10-05: praise progress):
 * a zone finished (and the next one shown), every star in a zone, a mower, tool, boost or upgrade
 * unlocked, a rare find kept, a milestone reached. Played in order when leaving the results.
 */
export type Reveal =
  | { kind: 'allStars'; reward: ZoneReward }
  /** Every star in the whole campaign: Stellar Tie-Dye and its coins (owner, 2026-10-29). */
  | { kind: 'everyStar' }
  /** Every secret yard found: Secret Garden and its coins (owner, 2026-10-30). */
  | { kind: 'everyYard' }
  | { kind: 'rare'; item: string }
  /** The first squirrel or rabbit tagged, and its cosmetic. */
  | { kind: 'critterTag'; critter: CritterKind; item: string }
  /** The first share of the game: its coin gift. */
  | { kind: 'shareGift'; coins: number }
  /** The share that reached the goal: one of its cosmetics. */
  | { kind: 'shareReward'; item: string; shares: number }
  | { kind: 'mower'; id: MowerId }
  | { kind: 'tool'; id: ToolId }
  | { kind: 'boost'; id: BoostId }
  | { kind: 'upgrade'; id: UpgradeKind }
  /** Mastery levels opening with a zone (economy pass 2): each upgrade and the level (0-based). */
  | { kind: 'upgradeLevels'; levels: readonly { id: UpgradeKind; level: number }[] }
  /** The lawn builder on sale (economy pass 2). */
  | { kind: 'builder' }
  /** Tool upgrades opening with a zone: each tool and the level (0-based). */
  | { kind: 'toolUpgrades'; levels: readonly { id: ToolId; level: number }[] }
  | { kind: 'milestone'; id: string; title: string; detail: string }
  | { kind: 'zoneComplete'; zone: BiomeId; next: BiomeId | null };

/** What a campaign clear did, for its reveals. */
export interface ClearFacts {
  level: number;
  firstClear: boolean;
  zoneReward: ZoneReward | null;
  /** The whole campaign's every star, earned now. */
  grandReward?: boolean;
  /** The last secret yard, banked now. */
  yardsReward?: boolean;
  rareItem: string | null;
}

/**
 * The reveals for a finished campaign lawn, comparing the save before and after it: all-stars,
 * a rare find, unlocks, milestones, and last (it leads on) the zone finished with its successor.
 */
export function campaignReveals(
  before: Readonly<Save>,
  after: Readonly<Save>,
  clear: ClearFacts | null,
): Reveal[] {
  const out: Reveal[] = [];
  if (clear?.zoneReward) out.push({ kind: 'allStars', reward: clear.zoneReward });
  if (clear?.grandReward) out.push({ kind: 'everyStar' });
  if (clear?.yardsReward) out.push({ kind: 'everyYard' });
  if (clear?.rareItem) out.push({ kind: 'rare', item: clear.rareItem });
  out.push(...unlockReveals(campaignProgress(before), campaignProgress(after), after.fullGame));
  if (clear?.firstClear) {
    const zone = biomeForLevel(clear.level);
    if (clear.level === zone.levels[1]) {
      const next = BIOMES[BIOMES.indexOf(zone) + 1];
      out.push({ kind: 'zoneComplete', zone: zone.id, next: next?.id ?? null });
    }
  }
  return out;
}

/**
 * Everything that opens between two campaign progress levels (lawns cleared). Without the full
 * game, the mowers that come with it stay shut (they open when it is bought: `fullGameReveals`).
 */
export function unlockReveals(from: number, to: number, fullGame = true): Reveal[] {
  const opened = (level: number | undefined) => level !== undefined && level > from && level <= to;
  const out: Reveal[] = [];
  for (const id of MOWER_IDS) {
    const level = MOWER_DEFS[id].unlockLevel;
    if (opened(level) && (fullGame || !mowerNeedsFullGame(level))) out.push({ kind: 'mower', id });
  }
  for (const t of TOOLS)
    if (t.available && opened(t.unlockLevel)) out.push({ kind: 'tool', id: t.id });
  for (const b of BOOSTS) if (opened(b.unlockLevel)) out.push({ kind: 'boost', id: b.id });
  const kinds = Object.keys(UPGRADES) as UpgradeKind[];
  for (const kind of kinds) {
    if (opened(upgradeLevelOpensAfter(kind, 0) || undefined))
      out.push({ kind: 'upgrade', id: kind });
  }
  // Later levels of an upgrade, and tool upgrades, opening with a zone: one card each.
  const levels = kinds.flatMap((id) =>
    Object.entries(UPGRADE_LEVEL_OPENS[id] ?? {})
      .map(([level, after]) => ({ id, level: Number(level), after }))
      .filter((l) => l.level > 0 && opened(l.after)),
  );
  if (levels.length > 0) {
    out.push({ kind: 'upgradeLevels', levels: levels.map(({ id, level }) => ({ id, level })) });
  }
  const tools = TOOLS.flatMap((t) =>
    t.upgrades.flatMap((u, level) => (opened(u.opensAfter) ? [{ id: t.id, level }] : [])),
  );
  if (tools.length > 0) out.push({ kind: 'toolUpgrades', levels: tools });
  if (opened(BUILDER_SHOP.opensAfter)) out.push({ kind: 'builder' });
  return out;
}

// ── Milestones ───────────────────────────────────────────────────────────────────────────────

interface Milestone {
  id: string;
  title: string;
  detail: string;
  reached: (save: Readonly<Save>) => boolean;
}

const commas = (n: number): string => String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ',');

const LAWNS: [number, string][] = [
  [10, 'The neighbors are noticing.'],
  [25, 'Stripes are your signature.'],
  [50, 'Half a hundred, all striped.'],
  [100, 'A hundred lawns. Legendary.'],
  [200, 'Two hundred! Take a bow.'],
];
const TILES: [number, string][] = [
  [5000, 'That is a lot of grass.'],
  [10000, 'Enough to carpet a town.'],
  [25000, 'Clippings for miles.'],
  [50000, 'The blades have stories.'],
  [100000, 'Grass fears you now.'],
];
const STARS: [number, string][] = [
  [50, 'Shining bright.'],
  [100, 'A sky full of them.'],
  [200, 'Starry, starry lawns.'],
  [300, 'Collector of the heavens.'],
  [400, 'Nearly every one.'],
  // (Every star in the game has its own card: the everyStar reveal and its prize.)
];
const STREAKS: [number, string][] = [
  [3, 'Three days running.'],
  [7, 'A whole week of dailies.'],
  [14, 'Two weeks straight!'],
  [30, 'A month of mornings mowed.'],
];

/** Round numbers worth a cheer (each shown once; the save remembers which). */
export const MILESTONES: readonly Milestone[] = [
  ...LAWNS.map(([n, detail]) => ({
    id: `lawns-${n}`,
    title: `${n} lawns mowed!`,
    detail,
    reached: (s: Readonly<Save>) => s.stats.lawnsMowed >= n,
  })),
  ...TILES.map(([n, detail]) => ({
    id: `tiles-${n}`,
    title: `${commas(n)} tiles cut!`,
    detail,
    reached: (s: Readonly<Save>) => s.stats.tilesMowed >= n,
  })),
  ...STARS.map(([n, detail]) => ({
    id: `stars-${n}`,
    title: `${n} stars!`,
    detail,
    reached: (s: Readonly<Save>) => totalStars(s) >= n,
  })),
  ...STREAKS.map(([n, detail]) => ({
    id: `streak-${n}`,
    title: `${n}-day daily streak!`,
    detail,
    reached: (s: Readonly<Save>) => s.daily.streak.count >= n,
  })),
];

/**
 * Milestones reached and not yet shown, marked shown on the save draft (an old save's are marked
 * at boot by {@link startTracking}, so an old player is not buried in cheers).
 */
export function takeMilestones(draft: Save): Reveal[] {
  const shown = (draft.milestones ??= []);
  const fresh = MILESTONES.filter((m) => !shown.includes(m.id) && m.reached(draft));
  shown.push(...fresh.map((m) => m.id));
  return fresh.map((m) => ({ kind: 'milestone', id: m.id, title: m.title, detail: m.detail }));
}

// ── The Garage's NEW badge ───────────────────────────────────────────────────────────────────

/** An unlock's key in `save.garageSeen`. */
export const unlockKey = (r: Extract<Reveal, { kind: 'mower' | 'tool' | 'boost' | 'upgrade' }>) =>
  `${r.kind}:${r.id}`;

/** The mowers that open on buying the full game, given how far the player has got. */
export function fullGameReveals(save: Readonly<Save>): Reveal[] {
  const progress = campaignProgress(save);
  return MOWER_IDS.filter((id) => {
    const level = MOWER_DEFS[id].unlockLevel;
    return mowerNeedsFullGame(level) && progress >= level && !save.ownedMowers.includes(id);
  }).map((id) => ({ kind: 'mower', id }) as const);
}

/** Everything the player's campaign progress has unlocked so far, as garage keys. */
export function unlockedKeys(save: Readonly<Save>): string[] {
  return unlockReveals(-1, campaignProgress(save), save.fullGame).flatMap((r) =>
    r.kind === 'mower' || r.kind === 'tool' || r.kind === 'boost' || r.kind === 'upgrade'
      ? [unlockKey(r)]
      : [],
  );
}

/**
 * Starts the progress trackers on a save from before they existed (at boot): everything already
 * unlocked counts as seen in the Garage, every milestone already reached as cheered.
 */
export function startTracking(draft: Save): void {
  draft.garageSeen ??= unlockedKeys(draft);
  draft.milestones ??= MILESTONES.filter((m) => m.reached(draft)).map((m) => m.id);
}

/** Unlocked things the player has not looked at in the Garage yet (its NEW badge and tags). */
export function unseenUnlocks(save: Readonly<Save>): string[] {
  const seen = save.garageSeen ?? [];
  return unlockedKeys(save).filter((k) => !seen.includes(k));
}

/** Marks garage keys as seen on a save draft. */
export function markSeen(draft: Save, keys: readonly string[]): void {
  draft.garageSeen ??= [];
  for (const k of keys) if (!draft.garageSeen.includes(k)) draft.garageSeen.push(k);
}
